/**
 * Latched-modifier ball entry (PRD §6.8).
 *
 * The interaction, and why it is this and not nested modals: a scorer taps `NB`, the
 * chip lights up and *stays* lit, then he taps `2`. Everything he has latched is on
 * screen the whole time and one tap clears it. Nested sheets fail in the field
 * because they hide the ball you are describing behind the question you are answering,
 * and because a mis-tap two levels deep is unrecoverable in the two seconds before
 * the next delivery.
 *
 * **A run digit is the commit.** That is what keeps the common ball — a dot, a single,
 * a boundary — at exactly one tap, which is the whole ergonomic budget. Extras cost
 * two taps (`WD` then the runs), and a wicket routes through the sheet because a
 * dismissal needs facts a keypad cannot carry.
 *
 * Pure, and deliberately in a `.ts` file with no JSX: every function here is a
 * cricket-adjacent decision and belongs under test, not under a renderer.
 */
import { impossibleDismissalReason } from "@sportsos/engine";
import type { DismissalType, Extras, RuleSet, Runs } from "@sportsos/engine";

/** Every key on the pad that latches rather than committing. */
export type LatchKey = "wide" | "noball" | "bye" | "legbye" | "wicket";

export interface Latch {
  /** Wide and no-ball are mutually exclusive: a ball is one or the other. */
  readonly extra: "wide" | "noball" | null;
  readonly bye: "bye" | "legbye" | null;
  readonly wicket: boolean;
}

export const NO_LATCH: Latch = { extra: null, bye: null, wicket: false };

export const isLatched = (latch: Latch, key: LatchKey): boolean =>
  key === "wicket" ? latch.wicket : key === "bye" || key === "legbye" ? latch.bye === key : latch.extra === key;

export const anyLatched = (latch: Latch): boolean =>
  latch.extra !== null || latch.bye !== null || latch.wicket;

/**
 * Why this key cannot be latched right now, or null if it can.
 *
 * ADR-0011 puts enforcement at entry, and "unselectable" is only honest if the reason
 * is legible — a greyed-out button with no explanation reads as a bug, and the scorer
 * stops trusting the screen.
 */
export function latchBlockedReason(latch: Latch, key: LatchKey, ruleSet: RuleSet): string | null {
  if (key === "wide" && ruleSet.wideRule === "none") {
    return "this RuleSet does not play wides";
  }
  // Runs that are not off the bat are all wides when the ball was wide — byes cannot
  // be scored off one, so offering `B` there would put runs in the wrong column of the
  // extras line and mis-charge the bowler.
  if ((key === "bye" || key === "legbye") && latch.extra === "wide" && !isLatched(latch, key)) {
    return "byes cannot be scored off a wide";
  }
  if (key === "wide" && latch.bye !== null && !isLatched(latch, key)) {
    return "byes cannot be scored off a wide";
  }
  return null;
}

/** Toggle a key, enforcing the exclusions. Latching one side of a pair clears the other. */
export function toggleLatch(latch: Latch, key: LatchKey): Latch {
  switch (key) {
    case "wicket":
      return { ...latch, wicket: !latch.wicket };
    case "wide":
      return latch.extra === "wide"
        ? { ...latch, extra: null }
        : { ...latch, extra: "wide", bye: null };
    case "noball":
      return { ...latch, extra: latch.extra === "noball" ? null : "noball" };
    case "bye":
    case "legbye":
      return { ...latch, bye: latch.bye === key ? null : key };
  }
}

/** `NB + LB + W`, or empty. Rendered next to the pad so the latch is never invisible. */
export function latchLabel(latch: Latch): string {
  const parts: string[] = [];
  if (latch.extra === "wide") parts.push("WD");
  if (latch.extra === "noball") parts.push("NB");
  if (latch.bye === "bye") parts.push("B");
  if (latch.bye === "legbye") parts.push("LB");
  if (latch.wicket) parts.push("W");
  return parts.join(" + ");
}

/**
 * Turn a latch plus a run count into a delivery's `Runs`.
 *
 * The allocations are the part worth being careful about, because each one is a column
 * in the extras line and a decision about whose figures move:
 *
 * - **Wide `n`** → `wide: 1 + n`. The first run is the penalty and was not run, so a
 *   wide they ran two off is `wide: 3`. That distinction is what makes strike rotation
 *   right: `wide: 3` is two crossings and they keep their ends, `wide: 2` is one and
 *   they swap.
 * - **No-ball `n`** → the penalty plus `n` off the bat *to the striker*, unless a bye
 *   or leg bye is also latched, in which case the runs are the team's and the bowler is
 *   charged only the penalty.
 * - **Bye / leg bye `n`** → a legal delivery, runs to the team, nothing to the striker
 *   and nothing charged to the bowler.
 *
 * `total`, `legal`, `over` and `ballInOver` are advisory everywhere in this system —
 * `replay()` recomputes all of them and ignores what was recorded. They are filled in
 * honestly anyway, so a payload read out of the database by a human makes sense.
 */
export function buildRuns(latch: Latch, runs: number): Runs {
  const extras = { wide: 0, noball: 0, bye: 0, legbye: 0, penalty: 0 };
  let batter = 0;

  if (latch.extra === "wide") {
    extras.wide = 1 + runs;
  } else {
    if (latch.extra === "noball") extras.noball = 1;
    if (latch.bye === "bye") extras.bye = runs;
    else if (latch.bye === "legbye") extras.legbye = runs;
    else batter = runs;
  }

  // A boundary is only the batter's when the runs were credited to him. A wide that
  // reaches the fence is five wides and nothing else, and a four scored in byes is not
  // a batting boundary — crediting either would inflate a strike rate for a ball the
  // batter never hit.
  const boundary = batter >= 6 ? "six" : batter >= 4 ? "four" : null;

  return {
    batter,
    extras,
    total: batter + extras.wide + extras.noball + extras.bye + extras.legbye,
    boundary,
    overthrow: false,
    shortRun: 0,
  };
}

/**
 * The inverse of `buildRuns`: what the pad must have been showing to produce this ball.
 *
 * Needed because an edit sheet has to open on a ball that already exists, and the ledger
 * stores the *outcome* (`Runs`) rather than the taps. Reconstructing the taps means a scorer
 * fixing a leg bye sees `NB + LB` latched and `2` selected — the state he would have had if
 * he had got it right the first time — instead of a blank pad he has to re-describe from
 * memory.
 *
 * Exact for everything `buildRuns` can produce, which is everything the pad can enter. Two
 * fields it cannot carry back, because the pad has no way to set them: `overthrow` and
 * `shortRun`. Callers editing a ball must preserve those from the original rather than
 * rebuilding the whole `Runs` from this — see `corrections.ts`, which does.
 */
export function latchOf(
  // Only the two fields it reads, so the over strip's narrower view of a ball fits as well as
  // a stored `Runs` — `boundary`, `overthrow` and `shortRun` are none of this function's
  // business, and asking for them would force a caller to invent them.
  runs: { readonly batter: number; readonly extras: Extras },
  hasWicket: boolean,
): { latch: Latch; runs: number } {
  const { extras } = runs;
  const wicket = hasWicket;

  // A wide's first run is the penalty, so `wide: 3` was two tapped runs.
  if (extras.wide > 0) {
    return { latch: { extra: "wide", bye: null, wicket }, runs: extras.wide - 1 };
  }

  const extra = extras.noball > 0 ? ("noball" as const) : null;
  if (extras.bye > 0) return { latch: { extra, bye: "bye", wicket }, runs: extras.bye };
  if (extras.legbye > 0) return { latch: { extra, bye: "legbye", wicket }, runs: extras.legbye };
  return { latch: { extra, bye: null, wicket }, runs: runs.batter };
}

/** Every dismissal the wicket sheet offers, in the order a scorer looks for them. */
export const OFFERED_DISMISSALS: readonly DismissalType[] = [
  "bowled",
  "caught",
  "caught_behind",
  "caught_and_bowled",
  "lbw",
  "run_out",
  "stumped",
  "hit_wicket",
  "mankad",
  "obstructing_the_field",
  "hit_ball_twice",
];

/** `caught_behind` → `caught behind`. The enum is snake_case; a scorer is not. */
export const dismissalLabel = (type: DismissalType): string => type.replace(/_/g, " ");

/**
 * Which dismissals this ball could have been, each with the reason if not.
 *
 * The reason comes from the engine, never from a list held here. ADR-0011 makes the
 * client the place cricket law is *enforced* and the server the place it is
 * *detected*; if the two disagree the scorer is offered a dismissal that the server
 * then flags as an anomaly, and an unresolved anomaly blocks him from completing the
 * match. One function, called from both sides, is what makes that impossible.
 */
export function dismissalOptions(
  latch: Latch,
  freeHit: boolean,
  ruleSet: RuleSet,
): readonly { readonly type: DismissalType; readonly blocked: string | null }[] {
  const context = {
    wide: latch.extra === "wide",
    noBall: latch.extra === "noball",
    freeHit,
  };
  return OFFERED_DISMISSALS.map((type) => ({
    type,
    blocked: impossibleDismissalReason(type, context, ruleSet),
  }));
}

/**
 * Which end the dismissed batter was standing at, guessed from the runs completed.
 *
 * A batter who has completed an even number of runs is back where he started. The
 * scorer can override it, and for a run out he often must — the reason a run out
 * happens is usually the run that was *not* completed, and only he saw where the
 * batter ended up. `Wicket.end` is load-bearing: the fold places the survivor at the
 * other end, and getting it wrong credits every remaining ball of the over to the
 * wrong batter.
 */
export function likelyEnd(outIsStriker: boolean, runsCompleted: number): "strikers" | "non_strikers" {
  const backWhereTheyStarted = runsCompleted % 2 === 0;
  const atStrikersEnd = outIsStriker === backWhereTheyStarted;
  return atStrikersEnd ? "strikers" : "non_strikers";
}

/**
 * Dismissals where the scorer must name at least one fielder.
 *
 * `caught_and_bowled` is absent on purpose: the fielder *is* the bowler, so the sheet
 * fills it in rather than asking a question with one possible answer.
 */
const NEEDS_FIELDER = new Set<DismissalType>([
  "caught",
  "caught_behind",
  "stumped",
  "run_out",
  "mankad",
]);

export const needsFielder = (type: DismissalType): boolean => NEEDS_FIELDER.has(type);

/**
 * Dismissals where the batter out is not the striker by default.
 *
 * A mankad is a run out of the *non-striker* — that is the only thing distinguishing
 * it from `run_out` in the ledger (see `events.ts`), so preselecting the striker there
 * would make the default actively wrong rather than merely unhelpful.
 */
export const defaultsToNonStriker = (type: DismissalType): boolean => type === "mankad";
